import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { UserAdditionalInfo } from '@/shared/features/rbac/components/user-select/user-additional-info/user-additional-info';
import { TUserSelectProps } from '@/shared/features/rbac/components/user-select/user-select.types';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useReadUsersForSelectQuery } from '@/shared/redux/api/ident/ident-api';
import {
  ReadUsersForSelectQuery,
  UserForSelectFragment,
} from '@/shared/types/types.generated';
import { getFullname } from '@/shared/utils/format';

export const UserSelect = <TFieldValues extends FieldValues, TOptionShape>({
  name,
  label,
  userFilter,
  createOption,
  getItemId,
  disableTags,
  additionalOptions = [],
  ...rest
}: TUserSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const { userSearchField } = useRbacConfig();
  const createDefaultOption = (user: UserForSelectFragment) => {
    return {
      label: getFullname(user),
      value: user.userId,
      additionalInfo: <UserAdditionalInfo user={user} />,
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadUsersForSelectQuery) => {
    if (!data) {
      return additionalOptions;
    }

    const users = data.readAllUsers.items;
    const validUsers = userFilter
      ? users.filter((user) => userFilter(user))
      : users;

    return [
      ...additionalOptions,
      ...validUsers.map((user) => _createOption(user)),
    ];
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadUsersForSelectQuery, TOptionShape>
      {...rest}
      name={name}
      label={label ?? t('rbac:user')}
      placeholder={t('rbac:search_user')}
      required
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadUsersForSelectQuery,
        transformData,
        disableNameLower: true,
        searchField: userSearchField,
      }}
      disableTags={disableTags}
    />
  );
};
